/* eslint-disable max-len */
/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { Entity, EntityClass } from '@moderepo/mode-internal-apis';
import { GenericTableComp, GenericTableCompProps, GenericMessageBox, capitalizeAll, BaseListCompDataItem }
    from '@moderepo/mode-app-components';
import { showSlideOutPanel, SlideOutPanelLocation, modeUIContext } from '@moderepo/mode-ui-state';
import { useSelectEntityClasses } from '../../utils/dataStateHooks';
import { useRitzTranslation } from '../../utils/customHooks';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../../utils/constants';
import { EntityClassPreviewCompOptions, EntitySlideOutPanelCompType } from './EntityClassPreviewContainer';
import { getEntityClassKeyPrefix, getEntityClassNameKey, getEntityClassDescriptionKey, getAttributeSchemaKeyPrefix,
    getAttributeSchemaLabelKey, getAttributeSchemaDescriptionKey, getAttributeSchemaOptionKeyPrefix, getAttributeSchemaOptionLabelKey,
    getMetricsDefinitionKeyPrefix, getMetricsDefinitionDisplayNameKey, getNumericMetricsDefinitionKeyPrefix, getNumericMetricDisplayNameKey,
    getNumericMetricUnitKey, getNumericMetricEvaluatorDisplayNameKey, getTagMetricsDefinitionKeyPrefix, getTagMetricDisplayNameKey,
    getEnumDefinitionKeyPrefix, getEnumDefinitionNameKey } from './entityClassesUtils';
import { ProjectTranslationPreviewCompOptions, ProjectTranslationSlideOutPanelCompType } from './ProjectTranslationPreviewContainer';
import { isEntitiesFeatureEnabled } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            width        : '100%',
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },
        button: {
            margin: theme.spacing(2),
        },

        resultContainer: {
            width     : '100%',
            height    : '100%',
            resize    : 'vertical',
            marginTop : theme.spacing(3),
            whiteSpace: 'nowrap',
            overflow  : 'scroll',
        },

        entityClassesList: {
            fontSize: 'large',
            margin  : theme.spacing(1, 0),
        },
    };
});


/**
 * Create translation keys/values for the entity class' displaySettings and add the new keys/values to the result object
 */
const addEntityClassDisplaySettingsKeysValues = (
    keyPrefix: string,
    displaySettings: EntityClass['displaySettings'],
    initializeValues: boolean,
    result: {
        [key: string]: string
    },
) => {
    result[getEntityClassNameKey(keyPrefix)] = initializeValues ? displaySettings.name : '';
    result[getEntityClassDescriptionKey(keyPrefix)] = initializeValues ? displaySettings.description : '';
};



/**
 * Create translation keys/values for all attribute schema and add the new keys/values to the result object
 */
const addEntityClassAttributeSchemaKeysValues = (
    keyPrefix: string,
    attributeSchema: EntityClass['attributeSchema'],
    initializeValues: boolean,
    result: {
        [key: string]: string
    },
) => {
    if (attributeSchema) {
        attributeSchema.forEach((schema) => {
            const attrKeyPrefix = getAttributeSchemaKeyPrefix(keyPrefix, schema.key);
            result[getAttributeSchemaLabelKey(attrKeyPrefix)] = initializeValues ? schema.label : '';
            result[getAttributeSchemaDescriptionKey(attrKeyPrefix)] = initializeValues ? schema.description ?? '' : '';

            if (schema.options) {
                schema.options.forEach((option) => {
                    const optionKeyPrefix = getAttributeSchemaOptionKeyPrefix(attrKeyPrefix, option.value.toString());
                    result[getAttributeSchemaOptionLabelKey(optionKeyPrefix)] = initializeValues ? option.label : '';
                });
            }
        });
    }
};


/**
 * Create translation keys/values for all the metrics definitions and add the new keys/values to the result object
 */
const addEntityClassMetricsDefinitionsKeysValues = (
    keyPrefix: string,
    metricsDefinitions: EntityClass['metricsDefinitions'],
    initializeValues: boolean,
    result: {
        [key: string]: string
    },
) => {
    if (metricsDefinitions) {
        metricsDefinitions.forEach((metricsDefinition) => {
            const metricsDefKeyPrefix = getMetricsDefinitionKeyPrefix(keyPrefix, metricsDefinition.metricsDefinitionId);
            result[getMetricsDefinitionDisplayNameKey(metricsDefKeyPrefix)] = initializeValues ? metricsDefinition.displayName ?? '' : '';

            if (metricsDefinition.numericMetrics) {
                metricsDefinition.numericMetrics.forEach((metric) => {
                    const metricKeyPrefix = getNumericMetricsDefinitionKeyPrefix(metricsDefKeyPrefix, metric.name);
                    result[getNumericMetricDisplayNameKey(metricKeyPrefix)] = initializeValues ? metric.displayName ?? '' : '';
                    if (metric.unit) {
                        result[getNumericMetricUnitKey(metricKeyPrefix)] = initializeValues ? metric.unit ?? '' : '';
                    }
                    if (metric.evaluator) {
                        result[getNumericMetricEvaluatorDisplayNameKey(metricKeyPrefix)] = initializeValues ? metric.evaluator.displayName : '';
                    }
                });
            }

            if (metricsDefinition.tagMetrics) {
                metricsDefinition.tagMetrics.forEach((metric) => {
                    const metricKeyPrefix = getTagMetricsDefinitionKeyPrefix(metricsDefKeyPrefix, metric.name);
                    result[getTagMetricDisplayNameKey(metricKeyPrefix)] = initializeValues ? metric.displayName ?? '' : '';
                    if (metric.enumDefinitions) {
                        metric.enumDefinitions.forEach((enumDef) => {
                            const enumKeyPrefix = getEnumDefinitionKeyPrefix(metricKeyPrefix, enumDef.value);
                            result[getEnumDefinitionNameKey(enumKeyPrefix)] = initializeValues ? enumDef.name : '';
                        });
                    }
                });
            }
        });
    }
};



/**
 * Create translation keys/values for ALL entity class
 */
const createTranslationKeysValuesForEntityClasses = (entityClasses: readonly EntityClass[], initializeValues: boolean): {
    [key: string]: string
} => {
    const result: {
        [key: string]: string
    } = {};

    entityClasses.forEach((entityClass) => {
        const keyPrefix = getEntityClassKeyPrefix(entityClass.entityClass);
        addEntityClassDisplaySettingsKeysValues(keyPrefix, entityClass.displaySettings, initializeValues, result);
        addEntityClassAttributeSchemaKeysValues(keyPrefix, entityClass.attributeSchema, initializeValues, result);
        addEntityClassMetricsDefinitionsKeysValues(keyPrefix, entityClass.metricsDefinitions, initializeValues, result);
    });

    return result;
};


interface ProjectEntityClassesSettingsContainerProps {
    readonly projectId: number;
}



export const ProjectEntityClassesSettingsContainer: React.FC<ProjectEntityClassesSettingsContainerProps> = (
    props: ProjectEntityClassesSettingsContainerProps,
) => {
    const { projectId } = props;
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, projectId);
    const classes = useStyle();
    const { trans } = useRitzTranslation(project);

    // Check if entities and alerting features enabled. We need these feature to enable the Alerts dashboard widget
    const entityFeatureEnabled = isEntitiesFeatureEnabled(project, undefined);

    const entityClasses = useSelectEntityClasses(props.projectId);
    const [selectedEntityClass, setSelectedEntityClass] = useState < string | undefined>();


    const showGeneratedTranslationJSON = useCallback((translationJSON: string) => {
        const options: ProjectTranslationPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : ProjectTranslationSlideOutPanelCompType.PROJECT_TRANSLATION_PREVIEW,
            compProps: {
                data: { translationJSON },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [uiDispatch]);


    /**
     * Create a translation JSON file that can be used for translating the attributes for all the entities.
     */
    const generateTranslationJSON = useCallback(() => {

        if (entityClasses) {

            const initializeValues = confirm('Do you want to initialize the translations with the values from the entity classes?');

            const languageCodes = SUPPORTED_LANGUAGES.map((language) => {
                return `'${language.code}'`;
            }).join(', ');

            // If the user selected "YES" for initialize the translation file with the values from the entity class definition then ask what language
            // to initialize it with. If nothing entered then use English.
            const defaultLanguageCode = initializeValues
                ? prompt(`Initialize the values for which language? Enter one of these ${languageCodes}`, DEFAULT_LANGUAGE.code) ?? DEFAULT_LANGUAGE.code
                : DEFAULT_LANGUAGE.code;

            // If the project already has some translation override then ask the user if they want to merge the project's translation with
            // the entity class' translation
            const mergeWithProjectTranslation = project?.supportToolSettings.translations
                ? confirm('Do you want to merge the entity class\' translation key/values with the project\'s translations?')
                : false;


            // Get all the translation keys/values for all the entity classes
            const entityClassesTranslations = createTranslationKeysValuesForEntityClasses(entityClasses, initializeValues);

            // Create a new translation JSON for all the supported languages and initialize it with the entity classes' translation
            const newResult = {
                // Merge the translation for the entity classes with the Project's translations override it "mergeWithProjectTranslation" is true
                ...(mergeWithProjectTranslation
                    ? JSON.parse(JSON.stringify(project?.supportToolSettings.translations ?? {}))
                    : {}
                ),
            };


            // Create a new map of translation key/value BUT without values. Only for the default language, we will use the one that has the value
            // initialized. For all other languages, we need to use empty values.
            const entityClassesTranslationsWithoutValues = Object.keys(entityClassesTranslations).reduce((result, key) => {
                return {
                    ...result,
                    [key]: '',
                };
            }, {});

            SUPPORTED_LANGUAGES.forEach((language) => {
                if (!newResult[language.code]) {
                    newResult[language.code] = {
                        override: {},
                    };
                }

                newResult[language.code].override = {
                    ...newResult[language.code]?.override,
                    ...(language.code === defaultLanguageCode ? entityClassesTranslations : entityClassesTranslationsWithoutValues),
                };
            });
            return JSON.stringify(newResult, undefined, 4);
        }
        return undefined;
    }, [entityClasses, project?.supportToolSettings.translations]);



    /**
     * Create the columns for the table. We will create 1 column for each item determined by the displaySections.
     */
    const fieldsSettings = useMemo((): GenericTableCompProps<Entity>['fieldsSettings'] => {
        if (entityClasses) {
            return {
                fields: {
                    entityClass: {
                        dataItemProp: 'entityClass',
                        label       : 'Entity Class',
                    },
                    name: {
                        dataItemProp: 'name',
                        label       : 'Name',
                    },
                    description: {
                        dataItemProp: 'description',
                        label       : 'Description',
                    },
                },
            };
        }
        return {
            fields: {},
        };
    }, [entityClasses]);


    /**
     * Used to render the information for each "entityClass" from entityClasses
     */
    const formattedEntityClassesData = useMemo((): readonly BaseListCompDataItem<EntityClass>[] | undefined => {
        if (entityClasses) {
            const result: BaseListCompDataItem<EntityClass>[] = [];
            entityClasses.forEach((entityClass) => {
                result.push({
                    actualValue : entityClass,
                    displayValue: {
                        entityClass: entityClass.entityClass,
                        name       : entityClass.displaySettings.name,
                        description: entityClass.displaySettings.description,
                    },
                    selected: selectedEntityClass === entityClass.entityClass,
                });
            });
            return result;
        }

        return undefined;
    }, [entityClasses, selectedEntityClass]);


    const onEntityClassSelected = useCallback((entityClass: EntityClass | undefined = undefined) => {
        setSelectedEntityClass(entityClass?.entityClass);
        const options: EntityClassPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : EntitySlideOutPanelCompType.ENTITY_CLASS_PREVIEW,
            compProps: {
                data: { projectId: props.projectId, entityClass },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);


    return (
        <div className={classes.root}>
            {!entityFeatureEnabled && (
                <GenericMessageBox
                    message="You have to enable entities feature if you want to use this page"
                    messageColor="error"
                />
            )}

            {/**
             * Display the list of entity classes that the component will use.
             */}
            <div className={classes.entityClassesList}>
                <GenericTableComp
                    title="Entity Classes"
                    icon="widgets"
                    headerless={false}
                    fieldsSettings={fieldsSettings}
                    listData={formattedEntityClassesData}
                    onListItemSelected={onEntityClassSelected}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.data', { count: 2 })),
                            })}
                        />
                    )}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.data', { count: 2 })),
                            })}
                        />
                    )}
                    customSecondaryActions={[{
                        label  : 'Create A New Entity Class',
                        onClick: onEntityClassSelected,
                    }, {
                        label  : 'Generate Project Translation JSON',
                        onClick: () => {
                            const translationJSON = generateTranslationJSON();
                            if (translationJSON !== undefined) {
                                showGeneratedTranslationJSON(translationJSON);
                            }
                        },
                    }]}
                />
            </div>
        </div>
    );
};
